import { Vector2 } from "../../common/vector2.js";

export type CatmulromMode = "axis_offset" | "perpendicular_normal";

export interface ICatmulromParams
{
  alpha   ?: number;
  ax_mult ?: number;
  ay_mult ?: number;
  mode    ?: CatmulromMode;
}

export class CatmulromPrecomputed
{
  public readonly p0: Vector2;
  public readonly p1: Vector2;
  public readonly p2: Vector2;
  public readonly p3: Vector2;
  public readonly bp1: Vector2;
  public readonly bp2: Vector2;

  /**
  * Функция вычисляет 4 контрольные точки (p1, bp1, bp2, p2) для сплайна Catmull-Rom.
  *
  * @param p0, p1, p2, p3 - Контрольные точки. Кривая идет от p1 до p2.
  * @param alpha  - Параметр «натяжения» сплайна (обычно 0.5).
  */
  constructor(
    p0    : Vector2,
    p1    : Vector2,
    p2    : Vector2,
    p3    : Vector2,
    alpha : number = 0.5
  )
  {
    this.p0 = p0;
    this.p1 = p1;
    this.p2 = p2;
    this.p3 = p3;
    const d1 = Math.hypot( p1.x - p0.x, p1.y - p0.y );
    const d2 = Math.hypot( p2.x - p1.x, p2.y - p1.y );
    const d3 = Math.hypot( p3.x - p2.x, p3.y - p2.y );

    const d1a  = Math.pow( d1, alpha );
    const d2a  = Math.pow( d2, alpha );
    const d3a  = Math.pow( d3, alpha );
    const d1a2 = Math.pow( d1, 2 * alpha );
    const d2a2 = Math.pow( d2, 2 * alpha );
    const d3a2 = Math.pow( d3, 2 * alpha );

    const A = 2 * d1a2 + 3 * d1a * d2a + d2a2;
    const B = 2 * d3a2 + 3 * d3a * d2a + d2a2;
    let N = 3 * d1a * ( d1a + d2a );
    let M = 3 * d3a * ( d3a + d2a );
    N = (N !== 0) ? 1 / N : 0;
    M = (M !== 0) ? 1 / M : 0;

    this.bp1 = new Vector2(
      ( -d2a2 * p0.x + A * p1.x + d1a2 * p2.x ) * N,
      ( -d2a2 * p0.y + A * p1.y + d1a2 * p2.y ) * N
    );
    this.bp2 = new Vector2(
      ( d3a2 * p1.x + B * p2.x - d2a2 * p3.x ) * M,
      ( d3a2 * p1.y + B * p2.y - d2a2 * p3.y ) * M
    );

    if ( this.bp1.isZeroVector() ) this.bp1.copyFrom( p1 );
    if ( this.bp2.isZeroVector() ) this.bp2.copyFrom( p2 );
  }

  /**
  * Функция возвращает координаты точки на сплайне Catmull-Rom в момент t.
  *
  * @param t - Значение от 0 до 1, где 0 соответствует p1, а 1 соответствует p2.
  * @return Координаты { x, y } точки на сплайне.
  */
  public catmullRomAtT( t: number ): Vector2
  {
    const mt = 1 - t;
    const mt2 = mt * mt;
    const mt3 = mt2 * mt;

    const t2 = t * t;
    const t3 = t2 * t;

    const mt2_t = 3 * mt2 * t;
    const mt_t2 = 3 * mt * t2;

    const x =
      mt3   * this.p1 .x +
      mt2_t * this.bp1.x +
      mt_t2 * this.bp2.x +
      t3    * this.p2 .x;

    const y =
      mt3   * this.p1 .y +
      mt2_t * this.bp1.y +
      mt_t2 * this.bp2.y +
      t3    * this.p2 .y;

    return new Vector2( x, y );
  }

  /** Вариант 1 (оригинальный): смещение по фиксированным осям X и Y */
  public static calcPrecomputedAxisOffset(
    from      : Vector2,
    to        : Vector2,
    amplitude : number,
    params   ?: ICatmulromParams
  ): CatmulromPrecomputed
  {
    const alpha   = params?.alpha   ?? 0.5;
    const ax_mult = params?.ax_mult ?? 25;
    const ay_mult = params?.ay_mult ?? 25;

    const ax = amplitude * ax_mult;
    const ay = amplitude * ay_mult;
    const dx = to.x - from.x;
    const dy = to.y - from.y;

    const p1 = new Vector2( from.x + dx / 3 + ax, from.y + dy / 3 - ay );
    const p2 = new Vector2( from.x + ( 2 * dx ) / 3 + ax, from.y + ( 2 * dy ) / 3 - ay );

    return new CatmulromPrecomputed( p1, from, to, p2, alpha );
  }

  /** Вариант 2 (новый): перпендикулярное смещение нормали к вектору движения */
  public static calcPrecomputedPerpendicular(
    from      : Vector2,
    to        : Vector2,
    amplitude : number,
    params   ?: ICatmulromParams
  ): CatmulromPrecomputed
  {
    const alpha = params?.alpha ?? 0.5;
    const dx = to.x - from.x;
    const dy = to.y - from.y;

    const len = Math.hypot( dx, dy );
    let nx = 0;
    let ny = 0;
    if ( len > 0 )
    {
      nx = -dy / len;
      ny = dx / len;
    }

    const offset_x = nx * amplitude;
    const offset_y = ny * amplitude;

    const p1 = new Vector2( from.x + dx / 3 + offset_x, from.y + dy / 3 + offset_y );
    const p2 = new Vector2( from.x + ( 2 * dx ) / 3 + offset_x, from.y + ( 2 * dy ) / 3 + offset_y );

    return new CatmulromPrecomputed( p1, from, to, p2, alpha );
  }

  public static calcPrecomputed( 
    from      : Vector2, 
    to        : Vector2, 
    amplitude : number, 
    params    ?: ICatmulromParams
  ): CatmulromPrecomputed
  {
    const mode = params?.mode ?? "axis_offset";
    if ( mode === "perpendicular_normal" )
      return this.calcPrecomputedPerpendicular( from, to, amplitude, params );

    return this.calcPrecomputedAxisOffset( from, to, amplitude, params );
  }
}
